import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Typography,
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { WeatherAlert } from 'types';
import { CloneWeatherAlertForm } from './types';
import {
  convertNumberFromDB,
  convertToImperial,
  getUnitForMeasurementType,
} from 'features/weather/utils/dataConverters';
import { getLabelForMeasurementType } from './utils/dataConverters';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useDishes } from 'features/dishes/api/getDishes';
import { useCreateWeatherAlert } from './api/createWeatherAlert';

interface Props {
  open: boolean;
  handleClose: () => void;
  weatherAlert: WeatherAlert | undefined;
}

const initialFormState: CloneWeatherAlertForm = {
  dish_id: '',
};

const WeatherAlertCloneDialog = ({
  open,
  handleClose,
  weatherAlert,
}: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<CloneWeatherAlertForm>({ defaultValues: initialFormState });
  const useImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const { data: unfilteredDishes } = useDishes({});
  const createAlert = useCreateWeatherAlert({});
  const dishes = unfilteredDishes?.filter(
    (dish) =>
      !dish.weatherAlerts.some(
        (alert) => alert.condition === weatherAlert?.condition
      )
  );

  const onSubmit = async () => {
    if (!weatherAlert) return;
    const alertForm = getValues();
    await createAlert.mutateAsync({
      ...weatherAlert,
      dish_id: Number(alertForm.dish_id),
    });
    reset(initialFormState);
    handleClose();
  };

  const handleCancel = () => {
    reset(initialFormState);
    handleClose();
  };

  if (!weatherAlert) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Clone Alert</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction='column'
          style={{ paddingTop: '.4rem' }}
        >
          <Grid item container justifyContent='space-around'>
            <Typography component='span'>
              {getLabelForMeasurementType(weatherAlert.condition)}{' '}
            </Typography>
            <Typography sx={{ ml: '1.5rem' }} color='orange' component='span'>
              Caution:{' '}
            </Typography>
            {weatherAlert.caution ? (
              <Typography component='span'>
                {convertToImperial(
                  convertNumberFromDB(
                    weatherAlert.caution,
                    weatherAlert.condition
                  ),
                  weatherAlert.condition,
                  useImperial
                )?.toFixed(2) +
                  ' ' +
                  getUnitForMeasurementType(
                    weatherAlert.condition,
                    useImperial
                  )}
              </Typography>
            ) : (
              <Typography>None</Typography>
            )}
            <Typography color='red' sx={{ ml: '1.5rem' }} component='span'>
              Warning:{' '}
            </Typography>

            <Typography component='span'>
              {convertToImperial(
                convertNumberFromDB(
                  weatherAlert.warning,
                  weatherAlert.condition
                ),
                weatherAlert.condition,
                useImperial
              )?.toFixed(2) +
                ' ' +
                getUnitForMeasurementType(weatherAlert.condition, useImperial)}
            </Typography>
          </Grid>
          <Grid item xs>
            <Controller
              name='dish_id'
              control={control}
              rules={{
                required: 'Dish is required.',
              }}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel required id='dish-select-label'>
                    Dish
                  </InputLabel>
                  <Select
                    labelId='dish-select-label'
                    label='Dish'
                    id='dish_id'
                    name='dish_id'
                    onChange={onChange}
                    value={value}
                    error={!!errors.dish_id}
                  >
                    {dishes &&
                      dishes?.map((dish) => (
                        <MenuItem key={dish.id} value={dish.id}>
                          {dish.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item container xs direction='column'>
            {Object.keys(errors).map((name) => (
              <ErrorMessage
                key={name}
                errors={errors}
                name={name as 'dish_id'}
                render={({ message }) => (
                  <Typography style={{ color: 'red', padding: 0, margin: 0 }}>
                    {message}
                  </Typography>
                )}
              />
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={() => handleSubmit(onSubmit)()}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeatherAlertCloneDialog;
